import {
  BATTLE_DT,
  SPECTATOR_SEAT,
  applyBattleResult,
  applyMove,
  createGame,
  playingFromSeats,
  type BattleResult,
  type GameState,
  type Replay,
  type ReplayDuel,
  type ServerMessage,
} from '@archon3d/core';
import type { RoomEvent } from '../net/roomClient';
import type { GameSession, RoomLink } from './session';

export type ReplayPace = 1 | 2 | 4;
export const REPLAY_PACES: readonly ReplayPace[] = [1, 2, 4];
/** Seconds between two moves at pace 1; the pieces' travel adds its own time. */
const MOVE_INTERVAL_S = 1.6;
/** The arena fades in before the first bundle is due, so the fight is not half over by then. */
const ARENA_LEAD_MS = 900;
/** How long a Card clash's cards lie face up before its outcome: as long as the server showed them. */
export const REVEAL_MS = 1400;
const TICK_POLL_MS = 33;

/** What the banner shows about the playback. */
export interface ReplayView {
  /** History entries shown so far, of the total. */
  index: number;
  total: number;
  playing: boolean;
  pace: ReplayPace;
  /** A duel is being fought in the arena right now. */
  inDuel: boolean;
  /** The whole game has been shown. */
  done: boolean;
  /** How the game ended, in words, once shown. */
  outcome: string | null;
}

export interface ReplayPlayerDeps {
  schedule?: (fn: () => void, ms: number) => () => void;
  now?: () => number;
}

interface DuelPlayback {
  duel: ReplayDuel;
  /** Bundles handed over so far. */
  sent: number;
  /** Fight time reached, ms; grows only while the playback advances. */
  elapsed: number;
  lastPoll: number;
  /** The arena is on and the bundles flow. */
  started: boolean;
  poll: (() => void) | null;
}

/**
 * Plays a finished game back by standing in for the server: the session gets the recorded
 * moves as `moved`, and each duel as `battle`, its bundles at the simulation rate and
 * `battleEnded`, so the same code path that follows a live game shows the replay. The player
 * paces itself by the session (the next entry goes out once the previous one is on the table),
 * at a chosen pace or one step at a time.
 */
export class ReplayPlayer implements RoomLink {
  readonly seat = SPECTATOR_SEAT;
  private readonly listeners = new Set<(event: RoomEvent) => void>();
  private readonly watchers = new Set<() => void>();
  private readonly schedule: NonNullable<ReplayPlayerDeps['schedule']>;
  private readonly now: () => number;
  private view: ReplayView;
  private session: GameSession | null = null;
  private unsubscribe: (() => void) | null = null;
  /** The state after each history entry, to tell when the session has shown one. */
  private states: GameState[] = [];
  private factionNames = new Map<string, string>();
  /** The next history entry to send. */
  private cursor = 0;
  /** The state the session must reach before the playback goes on. */
  private awaiting: GameState | null = null;
  private duel: DuelPlayback | null = null;
  /** A Card clash on show: its outcome goes out after a beat, as the server's did. */
  private reveal: { result: BattleResult; rng: number; timer: (() => void) | null } | null = null;
  private timer: (() => void) | null = null;
  /** Whether the entry under way keeps going (a pause freezes a duel mid-fight). */
  private advancing = false;
  private disposed = false;

  constructor(
    readonly replay: Replay,
    deps: ReplayPlayerDeps = {},
  ) {
    this.schedule =
      deps.schedule ??
      ((fn, ms) => {
        const handle = setTimeout(fn, ms);
        return () => clearTimeout(handle);
      });
    this.now = deps.now ?? (() => performance.now());
    this.view = {
      index: 0,
      total: replay.history.length,
      playing: false,
      pace: 1,
      inDuel: false,
      done: replay.history.length === 0,
      outcome: null,
    };
  }

  /** Nothing a watcher does reaches a game; the session never sends anything for one anyway. */
  sendMove(): void {}
  sendInput(): void {}
  sendForfeit(): void {}

  subscribe(listener: (event: RoomEvent) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** The banner follows the playback through this store. */
  onChange(listener: () => void): () => void {
    this.watchers.add(listener);
    return () => this.watchers.delete(listener);
  }

  getSnapshot(): ReplayView {
    return this.view;
  }

  /** The session built for this replay; the playback paces itself by what it has shown. */
  attach(session: GameSession): void {
    if (this.disposed) return;
    this.unsubscribe?.();
    this.session = session;
    const { ctx } = session.getSnapshot();
    const { seed, history } = this.replay;
    // The seats say which sides played: a side that sat out may be anywhere, not only last.
    let state = createGame(ctx, {
      seed,
      players: this.replay.summary.setup.players,
      playing: playingFromSeats(ctx.board.def, this.replay.summary.seats),
      engagement: this.replay.summary.setup.engagement,
    });
    this.states = history.map((entry) => {
      state =
        entry.kind === 'move'
          ? applyMove(ctx, state, entry.move)
          : applyBattleResult(ctx, state, entry.result, entry.rng);
      return state;
    });
    this.factionNames = new Map(ctx.board.def.factions.map((f) => [f.id, f.name]));
    this.unsubscribe = session.subscribe(() => this.onSession());
    // Playing from the start, after a beat: the board has time to appear before the first move.
    this.advancing = true;
    this.publish({ playing: true });
    this.scheduleNext();
  }

  play(): void {
    if (this.disposed || this.view.done) return;
    this.advancing = true;
    this.publish({ playing: true });
    this.showOutcome();
    if (this.idle) this.next();
  }

  pause(): void {
    this.timer?.();
    this.timer = null;
    this.holdReveal();
    this.advancing = false;
    this.publish({ playing: false });
  }

  /** Shows the next entry (a duel plays through, cards stay up for their beat) and waits. */
  step(): void {
    if (this.disposed || this.view.done) return;
    this.timer?.();
    this.timer = null;
    this.advancing = true;
    this.publish({ playing: false });
    this.showOutcome();
    if (this.idle) this.next();
  }

  setPace(pace: ReplayPace): void {
    this.publish({ pace });
    // A beat already counting down takes the new pace at once.
    if (this.timer) {
      this.timer();
      this.scheduleNext();
    }
    if (this.reveal?.timer) {
      this.holdReveal();
      this.showOutcome();
    }
  }

  dispose(): void {
    this.disposed = true;
    this.timer?.();
    this.timer = null;
    this.holdReveal();
    this.reveal = null;
    this.duel?.poll?.();
    this.duel = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.session = null;
    this.listeners.clear();
    this.watchers.clear();
  }

  /** Nothing is on its way to the table and no timer will send anything. */
  private get idle(): boolean {
    return (
      this.awaiting === null && this.timer === null && this.cursor < this.replay.history.length
    );
  }

  private next(): void {
    if (this.disposed || !this.session) return;
    const index = this.cursor;
    const entry = this.replay.history[index];
    const after = this.states[index];
    if (!entry || !after) return;
    this.cursor += 1;
    this.awaiting = after;
    if (entry.kind === 'move') {
      this.emit({ t: 'moved', move: entry.move, by: entry.by });
      return;
    }
    const duel = this.replay.duels.find((d) => d.index === index);
    if (!duel) {
      if (entry.card) {
        // A Card clash: its cards lie face up for a beat before the outcome, as they did live.
        // Sent together, the outcome cleared them before they were ever drawn.
        this.emit({ t: 'cardReveal', scores: entry.card });
        this.publish({ inDuel: true });
        this.reveal = { result: entry.result, rng: entry.rng, timer: null };
        this.showOutcome();
        return;
      }
      // A rolled battle: only its result was ever seen.
      this.emit({ t: 'battleEnded', result: entry.result, rng: entry.rng });
      return;
    }
    this.duel = {
      duel,
      sent: 0,
      elapsed: -ARENA_LEAD_MS,
      lastPoll: this.now(),
      started: false,
      poll: null,
    };
    this.publish({ inDuel: true });
    this.emit({
      t: 'battle',
      seed: duel.seed,
      attackerId: duel.attackerId,
      defenderId: duel.defenderId,
      humans: duel.humans,
      ...(duel.levels ? { levels: duel.levels } : {}),
    });
    this.onSession();
  }

  private onSession(): void {
    const session = this.session;
    if (!session || this.disposed) return;
    const snapshot = session.getSnapshot();
    const duel = this.duel;
    if (duel && !duel.started && snapshot.arena?.seed === duel.duel.seed) {
      // The arena is switching in: the bundles start flowing at the fight's own rate.
      duel.started = true;
      duel.lastPoll = this.now();
      duel.poll = this.schedule(() => this.pollDuel(), TICK_POLL_MS);
    }
    const target = this.awaiting;
    if (!target || !reached(snapshot.state, target)) return;
    this.awaiting = null;
    this.duel?.poll?.();
    this.duel = null;
    const done = this.cursor >= this.replay.history.length;
    this.publish({
      index: this.cursor,
      inDuel: false,
      done,
      outcome: done ? this.outcome() : null,
      playing: done ? false : this.view.playing,
    });
    if (done) return;
    if (this.view.playing) this.scheduleNext();
    else this.advancing = false;
  }

  /** Sends a clash's outcome once its cards have been up for their beat; a pause holds them. */
  private showOutcome(): void {
    const reveal = this.reveal;
    if (!reveal || reveal.timer || !this.advancing) return;
    reveal.timer = this.schedule(() => {
      this.reveal = null;
      this.emit({ t: 'battleEnded', result: reveal.result, rng: reveal.rng });
    }, REVEAL_MS / this.view.pace);
  }

  private holdReveal(): void {
    this.reveal?.timer?.();
    if (this.reveal) this.reveal.timer = null;
  }

  private scheduleNext(): void {
    this.timer = this.schedule(
      () => {
        this.timer = null;
        this.next();
      },
      (MOVE_INTERVAL_S * 1000) / this.view.pace,
    );
  }

  /** Hands over every bundle whose time has come; the arena steps them at its fixed rate. */
  private pollDuel(): void {
    const duel = this.duel;
    if (!duel || this.disposed) return;
    duel.poll = null;
    const now = this.now();
    if (this.advancing) duel.elapsed += (now - duel.lastPoll) * this.view.pace;
    duel.lastPoll = now;
    const due = Math.min(duel.duel.ticks.length, Math.floor(duel.elapsed / (BATTLE_DT * 1000)) + 1);
    while (duel.sent < due) {
      const bundle = duel.duel.ticks[duel.sent]!;
      duel.sent += 1;
      this.emit({ t: 'tick', ...bundle });
    }
    if (duel.sent >= duel.duel.ticks.length) {
      const entry = this.replay.history[duel.duel.index];
      if (entry?.kind === 'battle')
        this.emit({ t: 'battleEnded', result: entry.result, rng: entry.rng });
      return;
    }
    duel.poll = this.schedule(() => this.pollDuel(), TICK_POLL_MS);
  }

  private outcome(): string {
    const { winner } = this.replay.summary;
    if (winner === null) return 'The game was not finished';
    if (winner === 'draw') return 'The game is a draw';
    return `${this.factionNames.get(winner) ?? winner} wins`;
  }

  private emit(message: ServerMessage): void {
    for (const listener of [...this.listeners]) listener(message);
  }

  private publish(patch: Partial<ReplayView>): void {
    this.view = { ...this.view, ...patch };
    for (const watcher of [...this.watchers]) watcher();
  }
}

/** The session has shown an entry once its state carries the entry's turn and phase. */
function reached(state: GameState, target: GameState): boolean {
  return state.turn === target.turn && state.phase === target.phase;
}
